import {useApiQuery, useApiMutation, ApiResponse} from "../../../api/useApi";
import { useQueryClient } from "react-query";


// #################################################
//                      TYPES
// #################################################
/**
 * Loyalty Program type
 */
export type LoyaltyProgram = {
    id: number;
    use_level: boolean;
    level_progress_type?: 1 | 2 | null;
    use_point: boolean;
    label: string;
    internal_description?: string;
    loyalty_earning_type_id?: number;
    expense_quantity?: number;
    earn_quantity?: number;
    branding_id?: number | null;
    translates: LoyaltyProgramTranslation[];
    slug?: string;
    url?: string;
    images?: Image[];
    image?: Image | null; // image principale (la première de la liste)
    file_ids?: number[];
    retroactive_points_mode?: string;
    facility?: {
        id: number;
        name: string;
    }
}

export type Image = {
    id: number;
    url: string;
    name: string;
    order: number; // sort_order côté pivot
};

export type FacilityLoyaltyInfos = {
    is_loyalty_available: boolean;
    use_point: boolean;
    use_level: boolean;
    level_progress_type: 1 | 2 | null;
    earning_name: string | null;
    loyalty_program_id: number | null;
}

export type LoyaltyEarningType = {
    id: number;
    name: string;
}

// Nouvelle interface pour les traductions
export interface LoyaltyProgramTranslation {
    locale: string;
    public_description: string;
    earning_name: string;
    catalogs_title?: string | null;
    catalogs_description?: string | null;
}

export interface ProgramFormState {
    use_level: boolean;
    use_point: boolean;
    label: string;
    internal_description: string;
    loyalty_earning_type_id: number | null;
    expense_quantity: number | string;
    earn_quantity: number | string;
    branding_id?: number | null;
    translates: LoyaltyProgramTranslation[];
    level_progress_type?: 1 | 2;
    slug?: string;
    url?: string;
    images?: Image[];
    file_ids?: number[];
    retroactive_points_mode?: string;
}

// Récupérer tous les programmes de fidélité
export function useLoyaltyPrograms() {
    const url = "/loyalty-programs";
    return useApiQuery<LoyaltyProgram[]>(url);
}

// Récupérer un seul programme de fidélité par son ID
export function useLoyaltyProgram(id: string | undefined) {
    const url = `/loyalty-programs/${id}`;
    return useApiQuery<LoyaltyProgram>(url);
}

// Récupérer un seul programme de fidélité par son slug (public)
export function useLoyaltyProgramPublic(slug: string | undefined) {
    const url = slug ? `/loyalty-programs/public/${slug}` : '';
    return useApiQuery<LoyaltyProgram>(url, {}, {
        enabled: !!slug // Ne faire la requête que si le slug existe
    });
}

// Créer un programme de fidélité
export function useCreateLoyaltyProgram() {
    const queryClient = useQueryClient();

    return useApiMutation(
        "post",
        () => "/loyalty-programs",
        {
            onSuccess: () => {
                queryClient.invalidateQueries("/loyalty-programs");
            }
        }
    );
}

// Modifier un programme de fidélité
export function useUpdateLoyaltyProgram() {
    const queryClient = useQueryClient();
    return useApiMutation<
        Partial<LoyaltyProgram> & { catalogs?: { id: number; sort_order?: number; is_active?: boolean }[] },
        { id: number }
    >(
        "put",
        (variables) => `/loyalty-programs/${variables.id}`,
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries("/loyalty-programs");
                queryClient.setQueryData(`/loyalty-programs/${variables.id}`, data);
            }
        }
    );
}

// Supprimer un programme de fidélité
export function useDeleteLoyaltyProgram() {
    const queryClient = useQueryClient();

    return useApiMutation(
        "delete",
        (id) => `/loyalty-programs/${id}`,
        {
            onSuccess: () => {
                queryClient.invalidateQueries("/loyalty-programs");
            }
        }
    );
}

export function useLoyaltyEarningTypes() {
    const url = "/loyalty-earning-types";
    return useApiQuery<ApiResponse<LoyaltyEarningType[]>>(url, {}, {
        staleTime: Infinity, // Ces données sont statiques, pas besoin de les rafraîchir
    });
}

export function useFacilityLoyaltyInfos(facility_id?: number | string) {
    // On construit l'URL dynamiquement avec l'ID de l'établissement
    const url = facility_id ? `/facility-loyalty-infos/${facility_id}` : '';

    return useApiQuery<FacilityLoyaltyInfos>(
        url,
        {},
        {
            // On s'assure que la requête n'est activée que si l'ID existe.
            // C'est une double sécurité avec la vérification de l'URL.
            enabled: !!facility_id,
        }
    );
}


export function useLoyaltyInfos(loyalty_program_id?: number | string) {
    // On construit l'URL dynamiquement avec l'ID de l'établissement
    const url = loyalty_program_id ? `/loyalty-program-infos/${loyalty_program_id}` : '';

    return useApiQuery<FacilityLoyaltyInfos>(
        url,
        {},
        {
            // On s'assure que la requête n'est activée que si l'ID existe.
            // C'est une double sécurité avec la vérification de l'URL.
            enabled: !!loyalty_program_id,
        }
    );
}



// Types communs au payload des deux endpoints
export type LoyaltyCatalogsPayload = {
    translates: { locale: string; catalogs_title?: string | null; catalogs_description?: string | null }[];
    catalogs: { id: number; name: string; description?: string | null; image_url?: string | null }[];
};

// Program-centric: GET /loyalty-catalogs/{loyaltyProgramId}
export function useLoyaltyCatalogs(loyaltyProgramId?: string | number) {
    const id = loyaltyProgramId ? String(loyaltyProgramId) : "";
    const url = id ? `/loyalty-catalogs/${id}` : "";
    return useApiQuery<LoyaltyCatalogsPayload>(url, {}, { enabled: !!id });
}

// Facility-centric: GET /facility-loyalty-catalogs/{facilityId}
export function useFacilityLoyaltyCatalogs(facilityId?: number | string) {
    const url = facilityId ? `/facility-loyalty-catalogs/${facilityId}` : "";
    return useApiQuery<LoyaltyCatalogsPayload>(url, {}, { enabled: !!facilityId });
}

// #################################################
//              FACILITIES DU PROGRAMME
// #################################################

export type LoyaltyProgramFacilitiesResponse = {
    is_group: boolean;
    facilities: { id: number; name: string; city: string | null; zip_code: string | null }[];
    selected_ids: number[];
    mode: 'all' | 'selection';
};

export type LoyaltyPublicFacility = {
    id: number;
    name: string;
    city: string | null;
    zip_code: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    latitude: number | null;
    longitude: number | null;
};

// Admin: GET /loyalty-programs/{id}/facilities
export function useLoyaltyProgramFacilities(programId?: string | number) {
    const id = programId ? String(programId) : "";
    return useApiQuery<LoyaltyProgramFacilitiesResponse>(
        id ? `/loyalty-programs/${id}/facilities` : "",
        {},
        { enabled: !!id }
    );
}

// Admin: PUT /loyalty-programs/{id}/facilities
export function useSyncLoyaltyProgramFacilities(programId?: string | number) {
    const queryClient = useQueryClient();
    const id = programId ? String(programId) : "";
    return useApiMutation<{ mode: 'all' | 'selection'; facility_ids?: number[] }>(
        "put",
        () => `/loyalty-programs/${id}/facilities`,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(`/loyalty-programs/${id}/facilities`);
            },
        }
    );
}

// Public: GET /loyalty-programs/public/{slug}/facilities
export function useLoyaltyProgramPublicFacilities(slug?: string | null) {
    return useApiQuery<{ facilities: LoyaltyPublicFacility[] }>(
        slug ? `/loyalty-programs/public/${slug}/facilities` : "",
        {},
        { enabled: !!slug }
    );
}

// Vérifier la disponibilité d'un slug
export type SlugAvailability = {
    available: boolean;
    slug: string;
};

export function useCheckSlugAvailability(slug: string | undefined, currentProgramId?: string | number) {
    const url = slug ? `/loyalty-programs/check-slug/${slug}${currentProgramId ? `?current_program_id=${currentProgramId}` : ''}` : '';
    return useApiQuery<SlugAvailability>(url, {}, {
        enabled: !!slug && slug.length >= 2,
        staleTime: 5000, // Cache pendant 5 secondes
    });
}

